/**
 * NotesPanel - Unified notes component for any entity type
 * 
 * Supports: feature, goal, epic, initiative
 * 
 * Usage:
 *   <NotesPanel entityType="goal" entityId={goal.goal_id} teamMembers={teamMembers} />
 *   <NotesPanel entityType="feature" entityId={feature.feature_id} teamMembers={teamMembers} />
 * 
 * Features:
 *   - Rich text editing (bold, italic, lists, links)
 *   - @mentions with user autocomplete (type @ to trigger)
 *   - Reply threading with quoted text
 *   - Unread message tracking
 */
import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import tippy from 'tippy.js';
import { Send, Edit2, Trash2, X, Check, Reply, Bold, Italic, List, Link as LinkIcon, Loader2, Unlink, AtSign } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { unifiedNotesApi } from '../../services/api';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { formatDateSmart as formatDateSmartUtil, getBrowserTimezone } from '../../utils/dateUtils';

// ============ Mention Suggestion Component ============
const MentionList = forwardRef(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index) => {
    const item = items[index];
    if (item) {
      command({ id: item.user_id, label: item.name || item.email });
    }
  };

  // Ensure selectedIndex is within bounds
  const safeIndex = items.length > 0 ? Math.min(selectedIndex, items.length - 1) : 0;

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((prev) => (prev + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(safeIndex);
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border p-2 text-sm text-gray-500">
        No users found
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border overflow-hidden min-w-[200px]">
      {items.map((item, index) => (
        <button
          key={item.user_id}
          onClick={() => selectItem(index)}
          className={cn(
            "w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
            index === safeIndex 
              ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" 
              : "hover:bg-gray-50 dark:hover:bg-gray-700"
          )}
        >
          {item.avatar_url ? (
            <img src={item.avatar_url} alt="" className="w-6 h-6 rounded-full" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-xs font-medium text-blue-600 dark:text-blue-300">
              {(item.name || item.email)?.[0]?.toUpperCase() || '?'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{item.name || 'Unknown'}</div>
            {item.email && item.name && (
              <div className="text-xs text-gray-500 truncate">{item.email}</div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
});

MentionList.displayName = 'MentionList';

// Create mention suggestion configuration
const createMentionSuggestion = (teamMembers, currentUserId) => ({
  items: ({ query }) => {
    return teamMembers
      .filter((member) => {
        // Filter out current user - no need to @ yourself
        if (member.user_id === currentUserId) return false;
        const searchText = (member.name || member.email || '').toLowerCase();
        return searchText.includes(query.toLowerCase());
      })
      .slice(0, 8); // Limit to 8 suggestions
  },
  render: () => {
    let component;
    let popup;

    return {
      onStart: (props) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) return;

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
        });
      },
      onUpdate: (props) => {
        component?.updateProps(props);
        if (props.clientRect) {
          popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
        }
      },
      onKeyDown: (props) => {
        if (props.event.key === 'Escape') {
          popup?.[0]?.hide();
          return true;
        }
        return component?.ref?.onKeyDown(props);
      },
      onExit: () => {
        popup?.[0]?.destroy();
        component?.destroy();
      },
    };
  },
});

// Relative time formatter
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  // Use the date utility for older dates
  return formatDateSmartUtil(date, getBrowserTimezone());
};

// Link Popover Component
const LinkPopover = ({ editor, colors, isOpen, onOpenChange }) => {
  const [url, setUrl] = useState('');
  const inputRef = useRef(null);
  
  // Pre-fill URL when popover opens
  const handleOpenChange = (open) => {
    if (open) {
      const existingUrl = editor?.getAttributes('link').href || '';
      setUrl(existingUrl);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    onOpenChange(open);
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (url) {
      // Add https if no protocol
      const finalUrl = url.match(/^https?:\/\//) ? url : `https://${url}`;
      editor.chain().focus().setLink({ href: finalUrl }).run();
    }
    onOpenChange(false);
    setUrl('');
  };
  
  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    onOpenChange(false);
    setUrl('');
  };
  
  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "p-1.5 rounded transition-colors",
            editor?.isActive('link') ? colors.bgActive : colors.bgHover
          )}
        >
          <LinkIcon className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Link URL</label>
            <Input
              ref={inputRef}
              type="text"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" className="flex-1">
              {editor?.isActive('link') ? 'Update Link' : 'Add Link'}
            </Button>
            {editor?.isActive('link') && (
              <Button 
                type="button" 
                size="sm" 
                variant="outline"
                onClick={handleRemoveLink}
                className="text-red-500 hover:text-red-600"
              >
                <Unlink className="w-4 h-4" />
              </Button>
            )}
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
};

// Editor toolbar
const EditorToolbar = ({ editor, colors }) => {
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  
  if (!editor) return null;
  
  return (
    <div className="flex items-center gap-1 mb-2">
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={cn(
          "p-1.5 rounded transition-colors",
          editor.isActive('bold') ? colors.bgActive : colors.bgHover
        )}
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={cn(
          "p-1.5 rounded transition-colors",
          editor.isActive('italic') ? colors.bgActive : colors.bgHover
        )}
      >
        <Italic className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={cn(
          "p-1.5 rounded transition-colors",
          editor.isActive('bulletList') ? colors.bgActive : colors.bgHover
        )}
      >
        <List className="w-4 h-4" />
      </button>
      <LinkPopover 
        editor={editor} 
        colors={colors} 
        isOpen={linkPopoverOpen} 
        onOpenChange={setLinkPopoverOpen} 
      />
      <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
      <button
        onClick={() => editor.chain().focus().insertContent('@').run()}
        className={cn("p-1.5 rounded transition-colors", colors.bgHover)}
        title="Mention someone (@)"
      >
        <AtSign className="w-4 h-4" />
      </button>
    </div>
  );
};

// Single note component - simplified, editing happens in the bottom editor
const NoteItem = ({ note, isOwn, colors, onEdit, onDelete, onReply }) => {
  const handleDelete = async () => {
    if (window.confirm('Delete this note?')) {
      await onDelete(note.note_id);
    }
  };
  
  return (
    <div className={cn("flex gap-3 mb-4", isOwn ? "flex-row-reverse" : "flex-row")}>
      {/* Avatar */}
      <div className="flex-shrink-0">
        {note.author_avatar ? (
          <img 
            src={note.author_avatar} 
            alt={note.author_name} 
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium", colors.bgTertiary, colors.textPrimary)}>
            {note.author_name?.[0]?.toUpperCase() || '?'}
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className={cn("max-w-[75%]", isOwn ? "items-end" : "items-start")}>
        {/* Author & time */}
        <div className={cn("flex items-center gap-2 mb-1 text-xs", colors.textMuted, isOwn ? "justify-end" : "")}>
          <span className="font-medium">{isOwn ? 'You' : note.author_name}</span>
          <span>·</span>
          <span>{formatRelativeTime(note.created_at)}</span>
          {note.is_edited && <span className="italic">(edited)</span>}
        </div>
        
        {/* Reply quote */}
        {note.reply_to && (
          <div className={cn(
            "text-xs p-2 mb-1 rounded border-l-2 border-blue-400",
            colors.bgTertiary, colors.textMuted
          )}>
            <span className="font-medium">{note.reply_to.author_name}:</span> {note.reply_to.quoted_text}
          </div>
        )}
        
        {/* Note bubble - iMessage style */}
        <div className={cn(
          "rounded-2xl px-4 py-2",
          isOwn 
            ? "bg-[#007AFF] text-white rounded-br-sm" // iMessage blue
            : cn(colors.bgTertiary, colors.textPrimary, "rounded-bl-sm")
        )}>
          <div 
            className={cn(
              "prose prose-sm max-w-none",
              isOwn 
                // Own message: white text, cyan links, light mention pills
                ? "[&_a]:text-cyan-200 [&_a]:underline [&_.mention]:bg-white/20 [&_.mention]:text-white [&_p]:text-white [&_li]:text-white [&_strong]:text-white [&_em]:text-white"
                // Other's message: normal colors
                : "[&_a]:text-blue-600 [&_a]:underline [&_.mention]:bg-blue-100 [&_.mention]:text-blue-700"
            )}
            dangerouslySetInnerHTML={{ __html: note.content }}
          />
        </div>
        
        {/* Actions */}
        <div className={cn("flex items-center gap-2 mt-1", isOwn ? "justify-end" : "")}>
          <button 
            onClick={() => onReply(note)} 
            className={cn("text-xs flex items-center gap-1 hover:underline", colors.textMuted)}
          >
            <Reply className="w-3 h-3" /> Reply
          </button>
          {isOwn && (
            <>
              <button 
                onClick={() => onEdit(note)}
                className={cn("text-xs flex items-center gap-1 hover:underline", colors.textMuted)}
              >
                <Edit2 className="w-3 h-3" /> Edit
              </button>
              <button 
                onClick={handleDelete}
                className="text-xs flex items-center gap-1 hover:underline text-red-500"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Main NotesPanel component
const NotesPanel = ({ 
  entityType,  // 'feature' | 'goal' | 'epic' | 'initiative'
  entityId, 
  teamMembers = [], 
  colors: propColors,
  emptyMessage = "No notes yet",
  emptyDescription = "Start a conversation. Notes are visible to all team members.",
  onCountChange
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = propColors || getThemeColors();
  const { user } = useAuth();
  
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editingNote, setEditingNote] = useState(null); // Note being edited
  const [lastReadNoteId, setLastReadNoteId] = useState(null);

  useEffect(() => { onCountChange?.(notes.length); }, [notes.length, onCountChange]);
  
  const notesEndRef = useRef(null);
  const lastReadRef = useRef(null);
  
  // Tiptap editor setup with @mention support
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false, // Disable default Link to use custom configured Link below
      }),
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: 'Write a note... Use @ to mention someone' }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: createMentionSuggestion(teamMembers, user?.user_id),
      }),
    ],
    editorProps: {
      attributes: {
        class: cn('prose prose-sm max-w-none focus:outline-none min-h-[60px] p-3', colors.bgInput, colors.textPrimary),
      },
    },
  });
  
  // Start editing a note - load content into editor
  const startEditing = useCallback((note) => {
    setEditingNote(note);
    setReplyTo(null); // Clear any reply
    editor?.commands.setContent(note.content);
    editor?.commands.focus();
  }, [editor]);
  
  // Cancel editing
  const cancelEditing = useCallback(() => {
    setEditingNote(null);
    editor?.commands.clearContent();
  }, [editor]);
  
  // Load notes
  const loadNotes = useCallback(async () => {
    if (!entityType || !entityId) return;
    
    try {
      const res = await unifiedNotesApi.list(entityType, entityId);
      setNotes(res.data.notes || []);
      setLastReadNoteId(res.data.last_read_note_id);
    } catch (err) {
      console.error('Failed to load notes:', err);
      // Don't show toast for initial load failures
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);
  
  useEffect(() => {
    loadNotes();
  }, [loadNotes]);
  
  // Scroll to last read position or bottom
  useEffect(() => {
    if (!loading && notes.length > 0) {
      if (lastReadNoteId && lastReadRef.current) {
        lastReadRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        notesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [loading, notes.length, lastReadNoteId]);
  
  // Update read position when viewing notes
  useEffect(() => {
    if (notes.length > 0 && entityType && entityId) {
      const lastNote = notes[notes.length - 1];
      if (lastNote.note_id !== lastReadNoteId) {
        unifiedNotesApi.updateReadPosition(entityType, entityId, lastNote.note_id).catch(() => {});
      }
    }
  }, [notes, entityType, entityId, lastReadNoteId]);
  
  // Send note
  const handleSend = async () => {
    const content = editor?.getHTML();
    if (!content || content === '<p></p>') return;
    
    setSending(true);
    try {
      const noteData = { 
        entity_type: entityType,
        entity_id: entityId,
        content 
      };
      
      if (replyTo) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = replyTo.content;
        const quotedText = tempDiv.textContent?.substring(0, 100) || '';
        
        noteData.reply_to = {
          note_id: replyTo.note_id,
          quoted_text: quotedText + (quotedText.length >= 100 ? '...' : ''),
          author_name: replyTo.author_name
        };
      }
      
      const res = await unifiedNotesApi.create(noteData);
      setNotes(prev => [...prev, res.data]);
      editor?.commands.clearContent();
      setReplyTo(null);
      
      setTimeout(() => {
        notesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err) {
      console.error('Failed to send note:', err);
      toast.error('Failed to send note');
    } finally {
      setSending(false);
    }
  };
  
  // Save edited note
  const handleSaveEdit = async () => {
    if (!editingNote) return;
    
    const content = editor?.getHTML();
    if (!content || content === '<p></p>') return;
    
    setSending(true);
    try {
      const res = await unifiedNotesApi.update(editingNote.note_id, { content });
      setNotes(prev => prev.map(n => n.note_id === editingNote.note_id ? res.data : n));
      toast.success('Note updated');
      setEditingNote(null);
      editor?.commands.clearContent();
    } catch (err) {
      console.error('Failed to update note:', err);
      toast.error('Failed to update note');
    } finally {
      setSending(false);
    }
  };
  
  // Delete note
  const handleDelete = async (noteId) => {
    try {
      await unifiedNotesApi.delete(noteId);
      setNotes(prev => prev.filter(n => n.note_id !== noteId));
      toast.success('Note deleted');
    } catch (err) {
      console.error('Failed to delete note:', err);
      toast.error('Failed to delete note');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full min-h-[400px]" data-testid={`notes-panel-${entityType}`}>
      {/* Notes list - takes remaining space */}
      <div className="flex-1 overflow-y-auto px-2 py-4">
        {notes.length === 0 ? (
          <div className={cn("flex flex-col items-center justify-center h-full", colors.textMuted)}>
            <div className="text-4xl mb-4">💬</div>
            <h3 className={cn("text-lg font-medium mb-2", colors.textPrimary)}>{emptyMessage}</h3>
            <p className="text-sm text-center max-w-xs">{emptyDescription}</p>
          </div>
        ) : (
          <>
            {notes.map((note, index) => {
              const isLastRead = note.note_id === lastReadNoteId;
              return (
                <div key={note.note_id} ref={isLastRead ? lastReadRef : null}>
                  <NoteItem
                    note={note}
                    isOwn={note.is_own}
                    colors={colors}
                    onEdit={startEditing}
                    onDelete={handleDelete}
                    onReply={setReplyTo}
                  />
                  {isLastRead && index < notes.length - 1 && (
                    <div className="flex items-center gap-2 my-4">
                      <div className={cn("flex-1 h-px", colors.borderPrimary)} />
                      <span className={cn("text-xs px-2", colors.textMuted)}>New messages below</span>
                      <div className={cn("flex-1 h-px", colors.borderPrimary)} />
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={notesEndRef} />
          </>
        )}
      </div>
      
      {/* Editing indicator */}
      {editingNote && (
        <div className={cn("mx-2 px-3 py-2 rounded-t-lg border-l-2 border-amber-400 flex items-start justify-between", colors.bgTertiary)}>
          <div className="flex-1 min-w-0">
            <span className={cn("text-xs font-medium text-amber-600 dark:text-amber-400")}>Editing note</span>
            <p className={cn("text-xs truncate", colors.textSecondary)}>
              {(() => {
                const div = document.createElement('div');
                div.innerHTML = editingNote.content;
                return div.textContent?.substring(0, 50) + '...';
              })()}
            </p>
          </div>
          <button onClick={cancelEditing} className={cn("p-1", colors.textMuted)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Reply preview */}
      {replyTo && !editingNote && (
        <div className={cn("mx-2 px-3 py-2 rounded-t-lg border-l-2 border-blue-400 flex items-start justify-between", colors.bgTertiary)}>
          <div className="flex-1 min-w-0">
            <span className={cn("text-xs font-medium", colors.textMuted)}>Replying to {replyTo.author_name}</span>
            <p className={cn("text-xs truncate", colors.textSecondary)}>
              {(() => {
                const div = document.createElement('div');
                div.innerHTML = replyTo.content;
                return div.textContent?.substring(0, 50) + '...';
              })()}
            </p>
          </div>
          <button onClick={() => setReplyTo(null)} className={cn("p-1", colors.textMuted)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Editor - fixed at bottom */}
      <div className={cn("border-t p-3 mt-auto", colors.borderPrimary)}>
        <EditorToolbar editor={editor} colors={colors} />
        <div className="flex gap-2">
          <div className={cn("flex-1 border rounded-lg overflow-hidden", colors.borderPrimary)}>
            <EditorContent editor={editor} />
          </div>
          {editingNote ? (
            // Edit mode: Cancel and Save buttons
            <div className="flex flex-col gap-1 self-end">
              <Button 
                onClick={handleSaveEdit} 
                disabled={sending}
                size="sm"
                data-testid="save-note-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                <span className="ml-1">Save</span>
              </Button>
              <Button 
                onClick={cancelEditing} 
                variant="ghost"
                size="sm"
                data-testid="cancel-edit-btn"
              >
                <X className="w-4 h-4" />
                <span className="ml-1">Cancel</span>
              </Button>
            </div>
          ) : (
            // Normal mode: Send button
            <Button 
              onClick={handleSend} 
              disabled={sending}
              className="self-end"
              data-testid="send-note-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotesPanel;
